import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TablePagination,
  TextField,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
  getClinicians,
  getClinicianProfileById,
  approveClinicianProfile,
  updateClinicianStatus,
} from "../../../api/services/api";
import EmptyScreen from "../../../components/EmptyScreen";
import Loader from "../../../components/Loader";
import Table from "../../../components/TableComponent";
import { RoleGuard } from "../../../components/RBAC";

export const ClinicianApproval = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(0);
  const [tabValue, setTabValue] = useState(0);
  const [selectedClinician, setSelectedClinician] = useState<any>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  const statusFilter =
    tabValue === 0 ? "PENDING" : tabValue === 1 ? "APPROVED" : "REJECTED";

  const { data, isLoading } = useQuery(
    [
      "clinicians-approval",
      {
        search: search,
        limit: limit,
        offset: limit * page,
        status: statusFilter,
      },
    ],
    getClinicians
  );

  const { mutate: approveMutate } = useMutation(approveClinicianProfile, {
    onSuccess: (res: any) => {
      queryClient.invalidateQueries("clinicians-approval");
      toast.success("Clinician approved successfully");
      setApproveDialogOpen(false);
    },
    onError: (res: any) => {
      toast.error(
        res?.response?.data?.message || "Failed to approve clinician"
      );
    },
  });

  const { mutate: updateStatusMutate } = useMutation(updateClinicianStatus, {
    onSuccess: (res: any) => {
      queryClient.invalidateQueries("clinicians-approval");
      toast.success("Clinician status updated successfully");
      setRejectDialogOpen(false);
    },
    onError: (res: any) => {
      toast.error(res?.response?.data?.message || "Failed to update status");
    },
  });

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: any) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewClick = (clinician: any) => {
    setSelectedClinician(clinician);
    setViewDialogOpen(true);
  };

  const handleApproveClick = (clinician: any) => {
    setSelectedClinician(clinician);
    setApproveDialogOpen(true);
  };

  const handleRejectClick = (clinician: any) => {
    setSelectedClinician(clinician);
    setRejectDialogOpen(true);
  };

  const confirmApprove = () => {
    if (selectedClinician) {
      approveMutate(selectedClinician.id);
    }
  };

  const confirmReject = () => {
    if (selectedClinician) {
      updateStatusMutate({ id: selectedClinician.id, status: "REJECTED" });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "APPROVED":
        return "success";
      case "PENDING":
        return "warning";
      case "REJECTED":
        return "error";
      default:
        return "default";
    }
  };

  const Columns = [
    {
      title: "Name",
      dataIndex: "fullName",
      key: "fullName",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Phone",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
    },
    {
      title: "Specialization",
      dataIndex: "specialization",
      key: "specialization",
      render: (colData: any) => colData || "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (colData: any) => (
        <Chip
          label={colData || "Pending"}
          color={getStatusColor(colData)}
          size="small"
        />
      ),
    },
    {
      title: "Actions",
      dataIndex: "id",
      key: "id",
      render: (colData: any, row: any) => (
        <Box display="flex" gap="10px">
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleViewClick(row)}
          >
            <VisibilityIcon />
          </IconButton>
          {row.status !== "APPROVED" && (
            <RoleGuard allowedRoles={["SUPER_ADMIN", "ADMIN"]}>
              <IconButton
                size="small"
                color="success"
                onClick={() => handleApproveClick(row)}
              >
                <CheckCircleIcon />
              </IconButton>
            </RoleGuard>
          )}
          {row.status !== "REJECTED" && (
            <RoleGuard allowedRoles={["SUPER_ADMIN", "ADMIN"]}>
              <IconButton
                size="small"
                color="error"
                onClick={() => handleRejectClick(row)}
              >
                <CancelIcon />
              </IconButton>
            </RoleGuard>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box my={2}>
      <Box mb={2} display="flex" gap={2} alignItems="center">
        <Typography variant="h3">Clinician Approval</Typography>
      </Box>

      <Tabs
        value={tabValue}
        onChange={(e, newValue) => setTabValue(newValue)}
        sx={{ mb: 2 }}
      >
        <Tab label="Pending" />
        <Tab label="Approved" />
        <Tab label="Rejected" />
      </Tabs>

      <Box mb={2}>
        <TextField
          label="Search by name, email, or phone"
          sx={{ width: "450px", padding: "0px" }}
          InputProps={{
            endAdornment: (
              <IconButton>
                <SearchIcon />
              </IconButton>
            ),
          }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Box>

      <Box>
        {data?.data?.count === 0 ? (
          <EmptyScreen type="clinicians" />
        ) : (
          <>
            {isLoading ? (
              <Loader />
            ) : (
              <Box mt={2}>
                <Table
                  key={"clinicians-approval"}
                  dataSource={data?.data?.data}
                  rowKey={"id"}
                  columns={Columns}
                />
                <Box
                  sx={{
                    marginTop: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <TablePagination
                    count={data?.data?.count || 0}
                    page={page}
                    onPageChange={handlePageChange}
                    rowsPerPage={limit}
                    onRowsPerPageChange={handleRowsPerPageChange}
                  />
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Clinician Details</DialogTitle>
        <DialogContent>
          {selectedClinician && (
            <Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Full Name
                </Typography>
                <Typography variant="body1">
                  {selectedClinician.fullName}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">
                  {selectedClinician.email}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Mobile Number
                </Typography>
                <Typography variant="body1">
                  {selectedClinician.mobileNumber}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Specialization
                </Typography>
                <Typography variant="body1">
                  {selectedClinician.specialization || "N/A"}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Bio
                </Typography>
                <Typography variant="body1">
                  {selectedClinician.bio || "No bio available"}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={selectedClinician.status || "Pending"}
                  color={getStatusColor(selectedClinician.status)}
                  size="small"
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
      >
        <DialogTitle>Approve Clinician</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to approve {selectedClinician?.fullName}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmApprove} variant="contained" color="success">
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
      >
        <DialogTitle>Reject Clinician</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reject {selectedClinician?.fullName}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmReject} variant="contained" color="error">
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
